import { useMemo, useState, type CSSProperties } from 'react';
import confetti from 'canvas-confetti';
import { motion } from 'framer-motion';
import { Gift, LockOpen, RotateCcw } from 'lucide-react';
import { ImageWithFallback } from '../components/ImageWithFallback';
import { SectionHeading } from '../components/SectionHeading';
import { birthdayConfig } from '../config';
import { useLocalStorage } from '../hooks/useLocalStorage';

const COUPON_STORAGE_KEY = 'birthday-unlocked-coupons-v4';

const FLOWER_SYMBOLS = [
  '\u{1F338}',
  '\u{1F339}',
  '\u{1F33A}',
  '\u{1F337}',
  '\u{1F33C}',
  '\u{1F496}',
];

type FlowerParticle = {
  id: string;
  symbol: string;
  left: number;
  size: number;
  delay: number;
  duration: number;
  drift: number;
  rotation: number;
};

type FlowerStyle = CSSProperties & {
  '--flower-left': string;
  '--flower-size': string;
  '--flower-delay': string;
  '--flower-duration': string;
  '--flower-drift': string;
  '--flower-rotation': string;
};

function createFlowerRain(): FlowerParticle[] {
  const isMobile = window.matchMedia('(max-width: 760px)').matches;
  const count = isMobile ? 20 : 34;
  const seed = Date.now();

  return Array.from({ length: count }, (_, index) => ({
    id: `${seed}-${index}-${Math.random()}`,
    symbol: FLOWER_SYMBOLS[Math.floor(Math.random() * FLOWER_SYMBOLS.length)],
    left: Math.random() * 100,
    size: isMobile ? 18 + Math.random() * 17 : 20 + Math.random() * 23,
    delay: Math.random() * 0.65,
    duration: 2.2 + Math.random() * 1.45,
    drift: -90 + Math.random() * 180,
    rotation: -260 + Math.random() * 520,
  }));
}

export function CouponsSection() {
  const [unlocked, setUnlocked] = useLocalStorage<string[]>(COUPON_STORAGE_KEY, []);
  const [flowers, setFlowers] = useState<FlowerParticle[]>([]);
  const [announcement, setAnnouncement] = useState('');

  const unlockedCount = useMemo(
    () => birthdayConfig.coupons.filter((coupon) => unlocked.includes(coupon.id)).length,
    [unlocked],
  );

  const startFlowerRain = () => {
    const nextFlowers = createFlowerRain();
    setFlowers(nextFlowers);

    window.setTimeout(() => {
      setFlowers((current) =>
        current.length > 0 && current[0]?.id === nextFlowers[0]?.id ? [] : current,
      );
    }, 4300);

    confetti({
      particleCount: window.innerWidth <= 760 ? 18 : 28,
      spread: 70,
      startVelocity: 22,
      scalar: 0.8,
      origin: { y: 0.7 },
      colors: ['#ff4fa3', '#ffd1e8', '#ffd166', '#c453df'],
      disableForReducedMotion: true,
    });
  };

  const handleCouponClick = (id: string, title: string) => {
    const isAlreadyUnlocked = unlocked.includes(id);

    if (!isAlreadyUnlocked) {
      setUnlocked((current) => [...current, id]);
      setAnnouncement(`${title} unlocked. Flower rain started.`);
    } else {
      setAnnouncement(`${title} selected again. Another flower rain started.`);
    }

    startFlowerRain();
  };

  const resetCoupons = () => {
    setUnlocked([]);
    setAnnouncement('All birthday coupons are locked again.');
  };

  return (
    <section className="section-shell coupons-section" id="coupons">
      <div className="page-container">
        <SectionHeading
          eyebrow={birthdayConfig.copy.coupons.eyebrow}
          title={birthdayConfig.copy.coupons.title}
          description={birthdayConfig.copy.coupons.description}
        />

        <div className="coupon-toolbar">
          <p aria-live="polite">
            <strong>{unlockedCount}</strong> of {birthdayConfig.coupons.length} birthday coupons unlocked
          </p>

          {unlockedCount > 0 && (
            <button type="button" className="coupon-reset-button" onClick={resetCoupons}>
              <RotateCcw size={17} aria-hidden="true" />
              Lock coupons again
            </button>
          )}
        </div>

        <p className="sr-only" aria-live="polite">
          {announcement}
        </p>

        <div className="coupon-grid" aria-label="Birthday gift coupons">
          {birthdayConfig.coupons.map((coupon, index) => {
            const isUnlocked = unlocked.includes(coupon.id);

            return (
              <motion.button
                type="button"
                key={coupon.id}
                className={`coupon-card ${isUnlocked ? 'unlocked' : 'locked'}`}
                aria-pressed={isUnlocked}
                aria-label={
                  isUnlocked
                    ? `${coupon.title}. Unlocked. Tap again for flower rain.`
                    : `Unlock ${coupon.title}`
                }
                onClick={() => handleCouponClick(coupon.id, coupon.title)}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                whileHover={{ y: -5 }}
                whileTap={{ scale: 0.97 }}
                viewport={{ once: true, amount: 0.15 }}
                transition={{ delay: (index % 4) * 0.06 }}
              >
                <motion.div
                  className="coupon-inner"
                  animate={{ rotateY: isUnlocked ? 180 : 0 }}
                  transition={{ duration: 0.58, type: 'spring', stiffness: 115, damping: 16 }}
                >
                  <div className="coupon-face coupon-front">
                    <Gift size={32} aria-hidden="true" />
                    <span>Birthday Coupon</span>
                    <h3>{coupon.title}</h3>
                    <small>{'Tap to unlock \u{1F49D}'}</small>
                  </div>

                  <div className="coupon-face coupon-back">
                    <ImageWithFallback
                      src={coupon.sticker}
                      alt=""
                      className="coupon-sticker"
                    />
                    <LockOpen size={21} aria-hidden="true" />
                    <h3>{coupon.title}</h3>
                    <p>{coupon.detail}</p>
                    <span>{'Unlocked forever \u{1F496}'}</span>
                    <small>{'Tap again for flower rain \u{1F338}'}</small>
                  </div>
                </motion.div>
              </motion.button>
            );
          })}
        </div>

        <p className="coupon-mobile-hint">Swipe sideways to see every coupon.</p>
      </div>

      {flowers.length > 0 && (
        <div className="coupon-flower-rain" aria-hidden="true">
          {flowers.map((flower) => {
            const style: FlowerStyle = {
              '--flower-left': `${flower.left}%`,
              '--flower-size': `${flower.size}px`,
              '--flower-delay': `${flower.delay}s`,
              '--flower-duration': `${flower.duration}s`,
              '--flower-drift': `${flower.drift}px`,
              '--flower-rotation': `${flower.rotation}deg`,
            };

            return (
              <span key={flower.id} className="coupon-falling-flower" style={style}>
                {flower.symbol}
              </span>
            );
          })}
        </div>
      )}
    </section>
  );
}